import * as React from 'react'

import { WithBadge } from '~ui/components/badges/WithBadge'
import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { IconCart } from '~ui/components/icons/specific/IconCart'
import { Text } from '~ui/components/typography/Text/Text'
import { TOuterShadow } from '~ui/styleGuide/shadows'

type TButtonIconProps = Pick<PropsOf<typeof ButtonIcon>, 'onClick' | 'state'>

type TProps = NoChildren &
  TestingProps &
  TButtonIconProps & {
    /**
     * the total number of items in the cart
     */
    cartItemCount: number
    /**
     * shadow and background color of the `CartButtonIcon` when `cartItemCount === 0`
     */
    emptyCartStyles?: {
      shadow?: TOuterShadow
      color?: PropsOf<typeof ButtonIcon>['bgColor']
    }
  }

const badgeOffset: PropsOf<typeof WithBadge>['offsetTop'] = `-5px`

/**
 * Button icon displaying a cart icon with badge showing cartItemCount.
 * If the cart is empty, the badge is not rendered.
 */
export const CartButtonIcon: React.FC<TProps> = ({
  cartItemCount,
  emptyCartStyles = {},
  dataTestId,
  ...buttonIconProps
}) => {
  const hasSomeItemInCart = cartItemCount > 0

  return (
    <WithBadge
      offsetRight={badgeOffset}
      offsetTop={badgeOffset}
      badgeContent={
        hasSomeItemInCart ? (
          <Text fontSize={`s12`} lineHeight={1}>
            {cartItemCount}
          </Text>
        ) : null
      }
    >
      <ButtonIcon
        {...buttonIconProps}
        dataTestId={dataTestId}
        icon={IconCart}
        iconSize={`1.85rem`}
        shadow={hasSomeItemInCart ? `topLong` : emptyCartStyles.shadow ?? `none`}
        bgColor={hasSomeItemInCart ? `conversion` : emptyCartStyles.color ?? `tertiary`}
        padding={`medium`}
        radius={`round`}
      />
    </WithBadge>
  )
}
